import { HttpErrorResponse } from "@angular/common/http";
import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from "@angular/core";
import { TranslocoService } from '@jsverse/transloco';
import { ConfirmationService } from "primeng/api";
import { DialogService, DynamicDialogRef } from "primeng/dynamicdialog";
import { ToastServiceManager } from "@services/toast-service-manager.service";
import { catchError, finalize, of, take, throwError } from "rxjs";
import { TemplateConstants } from "@constants/template-constants";
import { Attachment } from "@premise-hazard-models/attachment.model";
import { AttachmentInputModel } from "@premise-hazard-models/attachment-input.model";
import { IncludeAttachmentType } from "@premise-hazard-models/include-attachment-type.model";
import { PremiseHazard } from "@premise-hazard-models/premise-hazard.model";
import { PremiseHazardCommandsRestService } from "@services/rest/premise-hazard-command-rest.service";
import { PremiseHazardQueryRestService } from "@services/rest/premise-hazard-rest.service";
import { UserService } from "@services/user.service";
import { EmptyTextPipe } from "@pipes/empty-text.pipe";
import { AttachmentNameLimitPipe } from "@pipes/attachment-name-limit.pipe";
import { LoadingDialogComponent } from "@app/shared/loading-dialog/loading-dialog.component";
import { AttachmentUploadDialogComponent } from "./attachment-upload-dialog/attachment-upload-dialog.component";
import { FileSizePipe } from "../../pipes/file-size.pipe";
import { Contact } from "../../models/premise-hazard/contact.model";
import { ResponseItem } from "@models/response-item.model";
import { BaseViewComponent } from "@app/shared/base-view.component";

@Component({
  selector: 'app-view-premise-hazard-item',
  templateUrl: './premise-hazard-view.component.html',
  styleUrls: ['./premise-hazard-view.component.scss'],
  standalone: false,
  providers: [EmptyTextPipe, AttachmentNameLimitPipe, FileSizePipe]
})

export class PremiseHazardViewComponent extends BaseViewComponent implements OnInit, OnChanges {
  @Input() PremiseHazardInput?: PremiseHazard;
  @Input() CanCreateReports: boolean = false;

  @Output() premiseHazardModifiedEvent: EventEmitter<void> = new EventEmitter<void>();
  @Output() requestProccessing: EventEmitter<void> = new EventEmitter<void>();
  @Output() requestCompleted: EventEmitter<void> = new EventEmitter<void>();
  @Output() convertAttachmentEvent: EventEmitter<Attachment> = new EventEmitter<Attachment>();
  @Output() printClicked: EventEmitter<void> = new EventEmitter<void>();
  @Output() hazardReloaded: EventEmitter<ResponseItem<PremiseHazard>> = new EventEmitter<ResponseItem<PremiseHazard>>();
  @Output() hazardNotFound: EventEmitter<PremiseHazard> = new EventEmitter<PremiseHazard>();

  private selectedFile?: File;

  dateFormat: string = 'dd/MM/yyyy HH:mm a zzzz';

  public IsLoading: boolean = false;
  public infoCalloutType = TemplateConstants.InfoCalloutType;
  public warningCalloutType = TemplateConstants.WarningCalloutType;
  public errorCalloutType = TemplateConstants.ErrorCalloutType;

  private tooLargeMessage!: string;
  private uploadFailedMessage!: string;
  private uploadSuccessfulMessage!: string;
  private uploadCancelledMessage!: string;
  public NoCommentsText!: string;
  public NoPurgeTypeText!: string;
  private convertSuccessfulMessage!: string;
  private hazardDeletedMessage!: string;
  public PremiseHazard!: PremiseHazard;
  public Attachments: Attachment[] = [];
  public Contacts: Contact[] = [];
  public CanUpload: boolean = false;

  constructor(private confirmationService: ConfirmationService,
    private dialogService: DialogService,
    private toastServiceManager: ToastServiceManager,
    private commandService: PremiseHazardCommandsRestService,
    private queryService: PremiseHazardQueryRestService,
    cdr: ChangeDetectorRef,
    private transloco: TranslocoService,
    userService: UserService,
  ) {
    super(userService, cdr);
  }

  private loadTranslations(): void {
    this.transloco.selectTranslate('PremiseHazard.View.text.fileSizeError').pipe(take(1)).subscribe(res => this.tooLargeMessage = res);
    this.transloco.selectTranslate('PremiseHazard.View.toast.uploadFailed').pipe(take(1)).subscribe(res => this.uploadFailedMessage = res);
    this.transloco.selectTranslate('PremiseHazard.View.toast.uploadSuccessful').pipe(take(1)).subscribe(res => this.uploadSuccessfulMessage = res);
    this.transloco.selectTranslate('PremiseHazard.View.toast.uploadCancelled').pipe(take(1)).subscribe(res => this.uploadCancelledMessage = res);
    this.transloco.selectTranslate('PremiseHazard.View.text.noComments').pipe(take(1)).subscribe(res => this.NoCommentsText = res);
    this.transloco.selectTranslate('PremiseHazard.View.text.noPurgeType').pipe(take(1)).subscribe(res => this.NoPurgeTypeText = res);
    this.transloco.selectTranslate('PremiseHazard.View.toast.convertSuccessful').pipe(take(1)).subscribe(res => this.convertSuccessfulMessage = res);
    this.transloco.selectTranslate('PremiseHazard.View.toast.hazardDeleted').pipe(take(1)).subscribe(res => this.hazardDeletedMessage = res);
    this.transloco.selectTranslate('Formats.date.short').pipe(take(1)).subscribe(res => this.dateFormat = res);
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.setPremiseHazard(this.PremiseHazardInput);
    this.loadLatestPremiseHazard();
  }

  ngOnInit(): void {
    this.CanUpload = this.userService.hasUploadPermissions();
    this.loadTranslations();
    this.setSharingInfo(this.PremiseHazardInput);
    if (this.PremiseHazardInput) {
      this.setPremiseHazard(this.PremiseHazardInput);
    }
    this.loadLatestPremiseHazard();
  }

  setPremiseHazard(premiseHazard?: PremiseHazard): void {
    premiseHazard ??= this.PremiseHazardInput;
    if (!this.PremiseHazardInput) {
      return;
    }
    this.PremiseHazardInput = premiseHazard;
    this.PremiseHazard = premiseHazard!;

    if (this.PremiseHazard.attachments) {
      this.Attachments = this.PremiseHazard.attachments as Attachment[];
    } else {
      this.Attachments = [];
    }

    if (this.PremiseHazard.contacts) {
      this.Contacts = this.PremiseHazard.contacts as Contact[];
    } else {
      this.Contacts = [];
    }

    this.setSharingInfo(this.PremiseHazardInput);
  }

  addAttachmentClicked(): void {
    const ref = this.dialogService.open(AttachmentUploadDialogComponent, {
      header: this.transloco.translate('PremiseHazard.View.modal.uploadAttachment.header'),
      modal: true,
      closable: false,
      dismissableMask: false,
      closeOnEscape: false,
      width: '600px',
    });

    ref.onClose.pipe(take(1))
      .subscribe((result: { attachment: AttachmentInputModel; file: File } | null) => {
        if (result) {
          this.selectedFile = result.file;
          this.uploadFile(result.attachment);
        }
        else {
          this.toastServiceManager.warning(this.uploadCancelledMessage);
        }
      });
  }

  convertAttachmentClicked(attachment: Attachment): void {
    const loadingRef = this.openLoadingDialog();
    this.requestProccessing.emit();
    this.commandService.convertAttachment(
      this.PremiseHazardInput!.ccCustomer,
      this.PremiseHazardInput!.ccAgency,
      attachment.id
    )
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.toastServiceManager.success(this.convertSuccessfulMessage);
          this.requestCompleted.emit();
          this.premiseHazardModifiedEvent.emit();
          loadingRef.close();
        },
        error: () => {
          this.requestCompleted.emit();
          loadingRef.close();
        }
      });
  }

  deleteAttachmentClicked(attachment: Attachment): void {
    this.confirmationService.confirm({
      header: this.transloco.translate('PremiseHazard.View.modal.deleteAttachment.header'),
      message: this.transloco.translate('PremiseHazard.View.modal.deleteAttachment.content'),
      acceptIcon: 'ic_trash',
      rejectIcon: 'ic_remove',
      acceptLabel: this.transloco.translate('Buttons.delete'),
      rejectLabel: this.transloco.translate('Buttons.cancel'),
      accept: () => {
        const loadingRef = this.openLoadingDialog();
        this.requestProccessing.emit();
        this.commandService.deleteAttachment(this.PremiseHazardInput!.id, attachment.id)
          .pipe(take(1))
          .subscribe(() => {
            this.requestCompleted.emit();
            this.premiseHazardModifiedEvent.emit();
            loadingRef.close();
          });
      },
    });
  }

  private uploadFile(attachmentInput: AttachmentInputModel): void {
    const loadingRef = this.openLoadingDialog();
    this.requestProccessing.emit();
    const attachment: AttachmentInputModel = {
      description: attachmentInput.description,
      name: attachmentInput.name,
      file: this.selectedFile!,
    };

    this.commandService
      .uploadAttachment(this.PremiseHazardInput!.id, attachment)
      .pipe(take(1),
        catchError((err) => {
          if (err instanceof HttpErrorResponse) {
            if (err.status === 413) {
              this.toastServiceManager.criticalError(this.tooLargeMessage);
            } else {
              this.toastServiceManager.criticalError(this.uploadFailedMessage);
            }
          }
          loadingRef.close();
          return throwError(() => err);
        }))
      .subscribe(() => {
        this.toastServiceManager.success(this.uploadSuccessfulMessage);
        this.premiseHazardModifiedEvent.emit();
        this.requestCompleted.emit();
        loadingRef.close();
      });
  }

  public onFileChanged(event: any): void {
    if (event.target.files[0].size >= 1_000_000_000) {
      // handled internally by dialog now (kept for legacy callers if any)
      //1 GB max file size
      return;
    }
    this.selectedFile = event.target.files[0];
  }

  public onViewAttachmentClicked(attachment: Attachment): void {
    const loadingRef = this.openLoadingDialog();

    this.requestProccessing.emit();

    this.queryService.getAttachment(this.PremiseHazardInput!.id, attachment.id)
      .pipe(take(1), catchError(() => {
        loadingRef.close();
        this.requestCompleted.emit();

        return of();
      }))
      .subscribe(response => {
        loadingRef.close();
        this.requestCompleted.emit();
        window.open(response.uri_inline, '_blank');
      });
  }

  private openLoadingDialog(): DynamicDialogRef {
    return this.dialogService.open(LoadingDialogComponent, {
      closable: false,
      modal: true,
      dismissableMask: false,
      closeOnEscape: false,
      showHeader: false,
      data: { message: this.transloco.translate('PremiseHazard.View.modal.loadingModal.content') },
    });
  }

  private loadLatestPremiseHazard(): void {
    if (!this.PremiseHazardInput) {
      return;
    }
    const staleHazard = this.PremiseHazardInput;
    if (!this.shouldLoadItem(staleHazard.id)) {
      return;
    }
    this.IsLoading = true;
    this.queryService
      .getPremiseHazard(
        staleHazard.id,
        {
          includeAttachments: IncludeAttachmentType.none,
          includeExtensionData: false,
          includeSupportingDocuments: true,
        },
        true,
      )
      .pipe(
        take(1),
        catchError((err) => {
          if (err instanceof HttpErrorResponse && err.status === 404) {
            this.toastServiceManager.warning(this.hazardDeletedMessage);
            this.hazardNotFound.emit(staleHazard);
          }
          return of(undefined);
        }),
        finalize(() => (this.IsLoading = false)),
      )
      .subscribe((response) => {
        if (!response) {
          return;
        }
        this.eTag = response.ETag;
        this.setPremiseHazard(response.Item);
        this.hazardReloaded.emit(response);
      });
  }
}
